/**
 * ANCIENNE RÉDACTION D'UN ARTICLE — QA 09/09/2026, BUG 4.
 *
 * Une réponse peut citer « l'ancien article 1142 du Code civil » (obligation
 * de faire, abrogé par l'ordonnance de 2016). Le vérificateur interroge la
 * base officielle par NUMÉRO : il retrouve l'article 1142 ACTUEL (violence,
 * vices du consentement) et affiche une pastille « en vigueur » sur un texte
 * qui n'a plus rien à voir avec celui dont parle la réponse.
 *
 * Ce module, purement lexical (aucun réseau, aucun modèle), repère les
 * numéros cités explicitement dans leur ANCIENNE rédaction afin que l'affichage
 * n'affirme pas leur mise en vigueur.
 */

/** Normalise un numéro d'article pour comparaison souple. */
function norm(value: string): string {
  return (value || "").toLowerCase().replace(/[^0-9a-z]/g, "");
}

/**
 * Marqueurs placés AVANT le numéro : « ancien article 1142 », « article 1142
 * ancien », « l'article 1142, dans sa rédaction antérieure à la réforme ».
 */
const NUMBER_SRC = String.raw`(?:[LRDA]\.?\s?)?\d{1,4}(?:[-–.\s]?\d{1,4})*(?:\s?(?:bis|ter|quater))?`;

/**
 * Motifs à distance : le marqueur d'ancienneté doit se rapporter AU numéro
 * capté. Si le texte intercalaire mentionne un autre texte (« l'ancien texte »,
 * « ces dispositions », un autre article), l'abrogation vise vraisemblablement
 * celui-là — cas très courant : « l'article 1382 est devenu l'article 1240 du
 * Code civil, l'ancien texte étant abrogé ».
 */
const OTHER_SUBJECT = /\bancien|\bart(?:icle)?\b|\btextes?\b|\bdispositions?\b|\br[ée]dactions?\b|\bcelui|\bcelle/i;

type DistantPattern = { re: RegExp; numberGroup: number; gapGroup: number };

const DIRECT_PATTERNS: readonly RegExp[] = [
  new RegExp(String.raw`\bancien(?:ne)?s?\s+(?:article|art\.?)\s*(${NUMBER_SRC})`, "gi"),
  new RegExp(String.raw`\b(?:article|art\.?)\s*(${NUMBER_SRC})\s+ancien\b`, "gi"),
];

const DISTANT_PATTERNS: readonly DistantPattern[] = [
  {
    re: new RegExp(
      String.raw`\b(?:article|art\.?)\s*(${NUMBER_SRC})([^.;\n]{0,80}?)\b(?:abrog[ée]|dans sa r[ée]daction ant[ée]rieure|ant[ée]rieur(?:e|ement) [àa] la r[ée]forme|avant la r[ée]forme|avant l['’]ordonnance|dans sa version ant[ée]rieure)\b`,
      "gi",
    ),
    numberGroup: 1,
    gapGroup: 2,
  },
  {
    re: new RegExp(
      String.raw`\b(?:abrog[ée]|supprim[ée])\b([^.;\n]{0,60}?)\b(?:article|art\.?)\s*(${NUMBER_SRC})`,
      "gi",
    ),
    numberGroup: 2,
    gapGroup: 1,
  },
];

/** Numéros d'articles cités dans leur ancienne rédaction (clés normalisées). */
export function formerArticleNumbers(body: string): Set<string> {
  const out = new Set<string>();
  const text = body || "";
  for (const re of DIRECT_PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const k = norm(m[1]);
      if (k.length >= 2) out.add(k);
    }
  }
  for (const { re, numberGroup, gapGroup } of DISTANT_PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (OTHER_SUBJECT.test(m[gapGroup] ?? "")) continue;
      const k = norm(m[numberGroup]);
      if (k.length >= 2) out.add(k);
    }
  }
  return out;
}

/**
 * VRAI si la réponse mobilise ce numéro UNIQUEMENT dans son ancienne
 * rédaction. Si le corps cite aussi l'article sans marqueur d'ancienneté
 * (« l'article 1124 actuel »), on ne modifie rien : la pastille officielle
 * reste pertinente.
 */
export function isCitedAsFormerArticle(number: string, body: string): boolean {
  const key = norm(number);
  if (key.length < 2) return false;
  if (!formerArticleNumbers(body).has(key)) return false;
  // Occurrences « neutres » du même numéro : on compte les mentions totales et
  // on n'écarte la validation que si aucune mention neutre n'existe.
  const re = new RegExp(String.raw`(?:article|art\.?)\s*(${NUMBER_SRC})`, "gi");
  let neutral = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body || "")) !== null) {
    if (norm(m[1]) !== key) continue;
    const before = (body || "").slice(Math.max(0, m.index - 40), m.index);
    const after = (body || "").slice(m.index, m.index + 140);
    const marked =
      /\bancien(?:ne)?s?\s*$/i.test(before) ||
      /\bancien\b|\babrog|r[ée]daction ant[ée]rieure|avant la r[ée]forme|avant l['’]ordonnance|version ant[ée]rieure/i.test(
        after,
      );
    if (!marked) neutral += 1;
  }
  return neutral === 0;
}

/** Mention affichée à la place de « en vigueur » pour une ancienne rédaction. */
export const FORMER_ARTICLE_NOTE =
  "ancienne rédaction citée — l'article portant ce numéro aujourd'hui a un objet différent";
